import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Search, Sparkles, X } from 'lucide-react';
import type { LeanCategoria, LeanFrequenciaLabel, LeanLibraryItem } from '../../types/leanLibrary';
import { LEAN_CATEGORIAS, LEAN_FREQUENCIAS, LEAN_NIVEL_LABELS } from '../../types/leanLibrary';
import { getLibraryForUser } from '../../services/leanLibraryService';
import { countEnrichedRoutines, isEnrichedRoutine } from '../../services/leanLibraryEnrichment';
import { LeanLibraryConsultantPanel } from './LeanLibraryConsultantPanel';
import type { LstCargo } from '../../types';

interface LeanLibraryModalProps {
  open: boolean;
  onClose: () => void;
  lstCargo: LstCargo;
  roleName?: string | null;
  onSelect: (item: LeanLibraryItem) => void;
}

const selectCls =
  'h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-700 outline-none focus:border-slate-500 dark:border-slate-700 dark:bg-slate-900/40';

export const LeanLibraryModal: React.FC<LeanLibraryModalProps> = ({
  open,
  onClose,
  lstCargo,
  roleName,
  onSelect,
}) => {
  const [search, setSearch] = useState('');
  const [frequencia, setFrequencia] = useState<LeanFrequenciaLabel | ''>('');
  const [categoria, setCategoria] = useState<LeanCategoria | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const items = useMemo(
    () =>
      getLibraryForUser(lstCargo, roleName, {
        search,
        frequencia: frequencia || undefined,
        categoria: categoria || undefined,
      }),
    [lstCargo, roleName, search, frequencia, categoria],
  );

  const selectedItem = useMemo(
    () => items.find((i) => i.id === selectedId) ?? items[0] ?? null,
    [items, selectedId],
  );

  useEffect(() => {
    if (!open) return;
    setSelectedId((prev) => {
      if (prev && items.some((i) => i.id === prev)) return prev;
      return items[0]?.id ?? null;
    });
  }, [open, items]);

  if (!open) return null;

  const enrichedCount = countEnrichedRoutines();

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-label="Fechar" onClick={onClose} />
      <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-[var(--bg-surface)]">
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <div>
            <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-700">
              <BookOpen size={12} />
              Biblioteca Inteligente de Gestão Industrial
            </div>
            <h2 className="text-[15px] font-bold text-slate-900 dark:text-slate-100">
              Consultor Digital embarcado
            </h2>
            <p className="mt-0.5 text-[11px] text-slate-500">
              {roleName ?? lstCargo} · {items.length} rotina(s) · consultor completo em {enrichedCount} modelos
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={16} />
          </button>
        </header>

        <div className="grid shrink-0 grid-cols-1 gap-2 border-b border-slate-100 bg-slate-50/80 p-3 sm:grid-cols-4 dark:border-slate-800 dark:bg-slate-900/30">
          <div className="relative sm:col-span-2">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${selectCls} pl-7`}
              placeholder="Buscar rotina..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className={selectCls} value={frequencia} onChange={(e) => setFrequencia(e.target.value as LeanFrequenciaLabel | '')}>
            <option value="">Todas frequências</option>
            {LEAN_FREQUENCIAS.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <select className={selectCls} value={categoria} onChange={(e) => setCategoria(e.target.value as LeanCategoria | '')}>
            <option value="">Todas categorias</option>
            {LEAN_CATEGORIAS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr]">
          <div className="min-h-0 overflow-y-auto border-b border-slate-100 p-2 lg:border-b-0 lg:border-r dark:border-slate-800">
            {items.length === 0 ? (
              <p className="py-12 text-center text-[12px] text-slate-400">Nenhuma rotina encontrada para os filtros.</p>
            ) : (
              <div className="space-y-1">
                {items.map((item) => {
                  const active = selectedItem?.id === item.id;
                  const hasConsultant = isEnrichedRoutine(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedId(item.id)}
                      className={`w-full rounded-lg border p-2.5 text-left transition-colors ${
                        active
                          ? 'border-blue-400 bg-blue-50/60 dark:border-blue-600 dark:bg-blue-950/30'
                          : 'border-transparent hover:border-slate-200 hover:bg-slate-50 dark:hover:border-slate-700 dark:hover:bg-slate-900/40'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] font-semibold text-slate-900 dark:text-slate-100">{item.titulo}</p>
                          <p className="mt-0.5 line-clamp-1 text-[10px] text-slate-500">{item.descricao}</p>
                          <p className="mt-1 text-[9px] text-slate-400">
                            {item.nivelHierarquico.map((n) => LEAN_NIVEL_LABELS[n].split('—')[0].trim()).join(' · ')}
                          </p>
                        </div>
                        {hasConsultant && (
                          <Sparkles size={12} className="mt-0.5 shrink-0 text-emerald-600" aria-label="Consultor completo" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="min-h-[280px] lg:min-h-0">
            {selectedItem ? (
              <LeanLibraryConsultantPanel
                key={selectedItem.id}
                item={selectedItem}
                onApply={() => onSelect(selectedItem)}
              />
            ) : (
              <div className="flex h-full items-center justify-center p-8 text-[12px] text-slate-400">
                Selecione uma rotina para ver a orientação completa.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeanLibraryModal;
